export type QrDotShape = 'square' | 'rounded' | 'dots';

export type QrLogoScaleOption = 0 | 16 | 22 | 30;

export type QrErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';

export interface QrStyleConfig {
  readonly fgHex: string;
  readonly bgHex: string;
  readonly dotShape: QrDotShape;
  readonly showLogo: boolean;
  readonly logoScalePercent: QrLogoScaleOption;
  readonly logoLabel: string;
  readonly errorCorrectionLevel?: QrErrorCorrectionLevel;
  readonly quietZoneModules?: number;
}

export type QrStyleRuleCode =
  | 'ERROR_CORRECTION_NOT_H'
  | 'QUIET_ZONE_TOO_SMALL'
  | 'DARK_QUIET_ZONE'
  | 'INVERTED_POLARITY'
  | 'RED_ORANGE_FOREGROUND'
  | 'LOW_CONTRAST'
  | 'LOGO_TOO_LARGE'
  | 'DOT_SHAPE_DECODE_FAILED';

export interface QrStyleRuleViolation {
  readonly code: QrStyleRuleCode;
  readonly message: string;
}

export interface QrStyleRuleEvaluation {
  readonly status: 'valid' | string;
  readonly valid: boolean;
  readonly contrastRatio: number;
  readonly violations: readonly QrStyleRuleViolation[];
  readonly primaryMessage: string | null;
}

export const STYLED_QR_DISCLAIMER_LABEL =
  'Not verified for GS1 print; verify with an ISO/IEC 15415 verifier before printing on packaging';

const HASH = String.fromCharCode(35);

export function formatCssHex(rawHex: string): string {
  const clean = normalizeHexCode(rawHex);
  return `${HASH}${clean}`;
}

export function normalizeHexCode(input: string): string {
  const stripped = input.trim().replace(/^[^0-9a-fA-F]+/, '');
  if (/^[0-9a-fA-F]{3}$/.test(stripped)) {
    const c0 = stripped[0] ?? '0';
    const c1 = stripped[1] ?? '0';
    const c2 = stripped[2] ?? '0';
    return `${c0}${c0}${c1}${c1}${c2}${c2}`.toUpperCase();
  }
  if (/^[0-9a-fA-F]{6}$/.test(stripped)) {
    return stripped.toUpperCase();
  }
  return '000000';
}

export const DEFAULT_SAFE_QR_STYLE: QrStyleConfig = {
  fgHex: '000000',
  bgHex: 'FFFFFF',
  dotShape: 'square',
  showLogo: false,
  logoScalePercent: 16,
  logoLabel: 'MP',
  errorCorrectionLevel: 'H',
  quietZoneModules: 4,
};

export interface QrColorPreset {
  readonly id: string;
  readonly label: string;
  readonly hex: string;
}

export const QR_FOREGROUND_PRESETS: readonly QrColorPreset[] = [
  { id: 'black', label: 'Pure black (safest)', hex: '000000' },
  { id: 'navy', label: 'Deep navy', hex: '0F172A' },
  { id: 'forest', label: 'Dark forest green', hex: '064E3B' },
  { id: 'charcoal', label: 'Dark charcoal', hex: '1F2937' },
  { id: 'orange', label: 'Brand orange (fails retail red-light scanners)', hex: 'FF5A1F' },
  { id: 'red', label: 'Bright red (fails retail red-light scanners)', hex: 'DC2626' },
  { id: 'silver', label: 'Light grey (too low contrast)', hex: 'CBD5E1' },
  { id: 'white', label: 'White (inverted / light dots)', hex: 'FFFFFF' },
] as const;

export const QR_BACKGROUND_PRESETS: readonly QrColorPreset[] = [
  { id: 'white', label: 'Pure white (safest)', hex: 'FFFFFF' },
  { id: 'cream', label: 'Warm ivory', hex: 'FFFBEB' },
  { id: 'mist', label: 'Soft ice blue', hex: 'F0F9FF' },
  { id: 'slate', label: 'Medium grey (low contrast)', hex: '94A3B8' },
  { id: 'dark', label: 'Dark slate (inverted background)', hex: '0F172A' },
] as const;

export const QR_DOT_SHAPE_OPTIONS: readonly {
  readonly value: QrDotShape;
  readonly label: string;
}[] = [
  { value: 'square', label: 'Square (classic)' },
  { value: 'rounded', label: 'Rounded squares' },
  { value: 'dots', label: 'Circular dots' },
] as const;

export const QR_LOGO_SIZE_OPTIONS: readonly {
  readonly value: QrLogoScaleOption;
  readonly label: string;
}[] = [
  { value: 16, label: 'Small (16% — safe)' },
  { value: 22, label: 'Medium (22% — maximum safe size)' },
  { value: 30, label: 'Extra large (30% — covers too many dots)' },
] as const;

export function parseHexToRgb(hex: string): {
  readonly r: number;
  readonly g: number;
  readonly b: number;
} {
  const clean = normalizeHexCode(hex);
  const r = Number.parseInt(clean.slice(0, 2), 16);
  const g = Number.parseInt(clean.slice(2, 4), 16);
  const b = Number.parseInt(clean.slice(4, 6), 16);
  return {
    r: Number.isFinite(r) ? r : 0,
    g: Number.isFinite(g) ? g : 0,
    b: Number.isFinite(b) ? b : 0,
  };
}

function srgbChannelToLinear(channel255: number): number {
  const normalized = channel255 / 255;
  return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
}

/**
 * Computes relative luminance (0.0 darkest to 1.0 lightest) for contrast and quiet-zone checks.
 */
export function computeRelativeLuminance(hex: string): number {
  const { r, g, b } = parseHexToRgb(hex);
  return (
    0.2126 * srgbChannelToLinear(r) +
    0.7152 * srgbChannelToLinear(g) +
    0.0722 * srgbChannelToLinear(b)
  );
}

export function computeContrastRatio(fgHex: string, bgHex: string): number {
  const l1 = computeRelativeLuminance(fgHex);
  const l2 = computeRelativeLuminance(bgHex);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return Number(((lighter + 0.05) / (darker + 0.05)).toFixed(2));
}

/**
 * Detects red or warm orange foreground colours that reflect red-light illumination
 * and appear invisible to optical checkout scanners.
 */
export function isRedOrOrangeForeground(hex: string): boolean {
  const { r, g, b } = parseHexToRgb(hex);
  return r >= 170 && r > g * 1.35 && r > b * 1.8 && b < 130;
}

export interface EvaluateQrStyleRulesOptions {
  readonly decodePassed?: boolean;
}

/**
 * Evaluates a styled QR configuration against the styled QR rules:
 * 1. Error correction level H.
 * 2. Limit on how much of the symbol the logo may cover (at most 22%).
 * 3. Minimum contrast between dots and background (dark dots on light background, no red/orange dots, minimum 4.5:1 contrast).
 * 4. A light quiet zone of at least 4 modules around the symbol.
 * 5. Square modules by default; any other dot shape is allowed only if the decode test passes.
 *
 * Returns `status: 'valid'` when all rules pass, or a plain-language reason when any rule fails.
 */
export function evaluateQrStyleRules(
  style: QrStyleConfig,
  options?: EvaluateQrStyleRulesOptions,
): QrStyleRuleEvaluation {
  const fgLum = computeRelativeLuminance(style.fgHex);
  const bgLum = computeRelativeLuminance(style.bgHex);
  const contrastRatio = computeContrastRatio(style.fgHex, style.bgHex);
  const effectiveEcLevel = style.errorCorrectionLevel ?? 'H';
  const effectiveQuietZone = style.quietZoneModules ?? 4;
  const violations: QrStyleRuleViolation[] = [];

  if (effectiveEcLevel !== 'H') {
    violations.push({
      code: 'ERROR_CORRECTION_NOT_H',
      message:
        'Styled QR codes must use error correction level H so the symbol remains scannable with custom styling.',
    });
  }

  if (effectiveQuietZone < 4) {
    violations.push({
      code: 'QUIET_ZONE_TOO_SMALL',
      message: `The quiet zone around the QR code is ${String(effectiveQuietZone)} modules, which is too narrow. Keep a light quiet zone of at least 4 modules around the symbol.`,
    });
  }

  if (fgLum >= bgLum) {
    violations.push({
      code: 'INVERTED_POLARITY',
      message:
        'QR dots must be darker than the background. Light dots on a dark background cannot be read by most barcode and phone scanners.',
    });
  } else if (bgLum < 0.5) {
    violations.push({
      code: 'DARK_QUIET_ZONE',
      message:
        'The quiet zone and background must be a light colour of at least 4 modules so scanners can find the outer edges of the QR code.',
    });
  }

  if (isRedOrOrangeForeground(style.fgHex)) {
    violations.push({
      code: 'RED_ORANGE_FOREGROUND',
      message:
        'Red or orange QR dots reflect red checkout scanner light (660 nm) and look invisible to retail scanners. Choose a dark colour like black, navy, or forest green.',
    });
  }

  if (fgLum < bgLum && contrastRatio < 4.5) {
    violations.push({
      code: 'LOW_CONTRAST',
      message: `There is not enough contrast between the QR dots and the background (${contrastRatio.toFixed(1)}:1). Choose a darker dot colour or a lighter background so cameras can read the code.`,
    });
  }

  if (style.showLogo && style.logoScalePercent > 22) {
    violations.push({
      code: 'LOGO_TOO_LARGE',
      message: `The centre logo covers ${String(style.logoScalePercent)}% of the QR code, which hides too many data dots to scan reliably. Keep the logo at 22% or smaller.`,
    });
  }

  if (style.dotShape !== 'square' && options?.decodePassed === false) {
    violations.push({
      code: 'DOT_SHAPE_DECODE_FAILED',
      message: `Non-square dot shapes (${style.dotShape}) are allowed only when the QR decode test passes and returns the exact GS1 Digital Link URL. Switch back to square modules or adjust the style.`,
    });
  }

  const primaryMessage = violations[0]?.message ?? null;
  return {
    status: primaryMessage ?? 'valid',
    valid: violations.length === 0,
    contrastRatio,
    violations,
    primaryMessage,
  };
}

/**
 * Validates styled QR rules and returns `'valid'` or a plain-language reason.
 * Note: For non-square dot shapes (`rounded` or `dots`), `options.decodePassed` must be `true`.
 */
export function validateStyledQrRules(
  style: QrStyleConfig,
  options?: EvaluateQrStyleRulesOptions,
): 'valid' | string {
  const effectiveOptions: EvaluateQrStyleRulesOptions =
    style.dotShape !== 'square' && options?.decodePassed !== true
      ? { decodePassed: false }
      : (options ?? {});
  return evaluateQrStyleRules(style, effectiveOptions).status;
}

export {
  extractModuleCellsFromSvgPath,
  generateStyledQrAssets,
  verifyStyledQrDecode,
  type GeneratedStyledQrAssets,
} from './qr-styled-generator.js';
